import { type AITrace } from "@inrange/building-manager-api-client";
import { Badge, Button, Spinner } from "react-bootstrap";
import { customerDataFraction, renderFillCoverageBadge } from "./fillCoverage";

export interface OutputMetaData {
  date_of_analysis: string;
  combined_df_gaps: Array<{
    start: string;
    end: string;
    rows: number;
    method: string;
  }>;
  latest_full_year_num_rows: number;
  latest_full_year_num_rows_customer_data: number;
  typical_year_start: string;
  typical_year_end: string;
  typical_year_data_sum: number;
  input_file: string;
  site_mapping: Record<string, string>;
  input_data_start: string;
  input_data_end: string;
  fullyear_data_start: string;
  fullyear_data_end: string;
  largest_gap_days?: number;
  // Resolved UTC-vs-local interpretation (INRT-2372). timestamp_detection is present
  // only when auto-detection ran (absent on an explicit override / the default).
  timestamps_are_utc?: boolean;
  timestamp_detection?: {
    verdict: "utc" | "local" | "inconclusive";
    reason?: string;
    confidence_ratio?: number | null;
    transitions_analyzed?: number;
  };
}

export interface HhDataTempFile {
  fileGuid: string;
  fileName: string;
  uploadedAt: number;
  uploadedBy?: string;
  processedTime?: number;
  processedBy?: string;
  outputMetaData?: OutputMetaData;
  // Surfaced by /hh_data/temp from the per-file last_auto_operation.json S3 marker.
  // Lets any operator (not just the one who kicked the bulk run) see row-level
  // Auto-process state after a page reload.
  lastAutoOperationKey?: string;
  lastAutoOperationRanAt?: number;
  lastAutoOperationRanBy?: string;
  // Derived AI-assist state from the per-file attempt_history.json, surfaced so the
  // FE restores the AI badges + cached analysis across reloads and for any operator.
  autoAttemptCount?: number;
  autoFixAttempted?: boolean;
  autoFixed?: boolean;
  latestAnalysis?: {
    summary?: string;
    details?: string;
    proposed_config?: Record<string, unknown> | null;
    proposed_config_rationale?: string | null;
    trace?: AITrace | null;
  };
  // Persisted multi-curve detection (from multi_curve_detection.json). Present on a
  // file that /process_auto flagged as multi-curve and that hasn't been split yet -
  // lets the parked "review split" state rehydrate on reload for any operator.
  multiCurveCandidates?: Array<{
    label: string;
    config: Record<string, unknown>;
    source: "sheet" | "column" | "row_meter";
    confidence: "high" | "medium" | "low";
    reasons: string[];
  }>;
  multiCurveReason?: string;
  // Set when this file is one curve split out of a multi-curve source (split_provenance.json).
  isSplitChild?: boolean;
  splitLabel?: string;
}

interface DemandFileSummaryRowProps {
  file: HhDataTempFile;
  onProcessClick?: (file: HhDataTempFile) => void;
  onDeleteClick?: (file: HhDataTempFile) => void;
  onCommitClick?: (file: HhDataTempFile) => void;
  showActions?: boolean;
  isSelected?: boolean;
  onSelectionChange?: (checked: boolean) => void;
  // Per-row Auto-process phase. Drives the badge text + inline button affordances:
  //   processing              - initial /process_auto LRO in flight
  //   analysing               - LRO failed; first AI analysis in flight
  //   reanalysing             - auto-fix failed; AI re-analysing (history-aware)
  //   reprocessing            - AI proposed a fix; auto-applying via /process
  //   failed_with_analysis    - first analysis complete, no fix auto-applied yet; review
  //   failed_with_updated_fix - auto-fix failed; AI has an updated fix for manual apply
  //   failed_no_analysis      - LRO failed; AI analysis hasn't returned yet (or failed)
  //   autofixed               - healed via an AI-proposed fix (success badge)
  //   multi_curve             - one file holding several curves; parked for split review
  autoStatus?:
    | "processing"
    | "analysing"
    | "reanalysing"
    | "reprocessing"
    | "failed_with_analysis"
    | "failed_with_updated_fix"
    | "failed_no_analysis"
    | "autofixed"
    | "multi_curve";
  // True when the cached AI analysis has a proposed_config the operator can re-apply.
  // Controls visibility of the [Reprocess with proposed fix] button.
  hasProposedFix?: boolean;
  // True when there's a cached AI analysis to review (even for a healed/auto-fixed
  // row), enabling the [Review file analysis] button.
  hasAnalysis?: boolean;
  // Number of processing attempts so far this invocation - shown as "(attempt N)".
  // Informational only; no bound is enforced.
  attemptCount?: number;
  onReviewAnalysis?: () => void;
  onReprocessWithProposedFix?: () => void;
  onReviewSplit?: () => void;
}

const DemandFileSummaryRow = ({
  file,
  onProcessClick,
  onDeleteClick,
  onCommitClick,
  showActions = true,
  isSelected = false,
  onSelectionChange,
  autoStatus,
  hasProposedFix = false,
  hasAnalysis = false,
  attemptCount,
  onReviewAnalysis,
  onReprocessWithProposedFix,
  onReviewSplit,
}: DemandFileSummaryRowProps) => {
  const formatDate = (epochSeconds: number) => {
    return new Date(epochSeconds * 1000).toLocaleString();
  };

  const formatDateString = (dateStr: string | undefined) => {
    if (!dateStr) return "—";
    // Handle ISO format dates
    return dateStr.split("T")[0];
  };

  const formatDateTime = (epochSeconds: number | undefined) => {
    if (!epochSeconds) return "—";
    // Convert epoch seconds to milliseconds for JavaScript Date
    return new Date(epochSeconds * 1000).toLocaleString();
  };

  const formatNumber = (num: number | undefined) => {
    if (num === undefined) return "—";
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const coverageFraction = customerDataFraction(file.outputMetaData);

  const isProcessed = !!file.outputMetaData;

  // Informational attempt counter; never enforces a bound. Only meaningful once
  // there's been more than one attempt (i.e. an auto-fix was tried).
  const attemptSuffix =
    attemptCount && attemptCount > 1 ? ` (attempt ${attemptCount})` : "";

  // The in-flight (spinner) badges share a shape; only the label differs.
  const spinnerLabel: string | null =
    autoStatus === "processing"
      ? "Processing..."
      : autoStatus === "analysing"
        ? "Auto-process failed - AI analysis running"
        : autoStatus === "reanalysing"
          ? `Auto-process failed - AI fix attempted and still failed - re-analysing${attemptSuffix}`
          : autoStatus === "reprocessing"
            ? `Auto-process failed - AI analysis completed - auto-fix available, reprocessing${attemptSuffix}`
            : null;
  const autoSpinnerBadge = spinnerLabel ? (
    <Badge
      bg={autoStatus === "processing" ? "info" : "danger"}
      className="mt-1"
    >
      <Spinner
        animation="border"
        size="sm"
        style={{ width: 10, height: 10, borderWidth: 2 }}
        className="me-1"
      />
      {spinnerLabel}
    </Badge>
  ) : null;

  return (
    <tr>
      {onSelectionChange && (
        <td>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onSelectionChange(e.target.checked)}
          />
        </td>
      )}
      <td>
        <div>{file.fileName}</div>
        <div style={{ fontSize: "0.7em", color: "#666" }}>{file.fileGuid}</div>
        {file.isSplitChild && (
          <Badge
            bg="info"
            className="mt-1"
            title={
              file.splitLabel
                ? `Split from a multi-curve file - curve "${file.splitLabel}"`
                : "One curve split out of a multi-curve file"
            }
          >
            Multi-curve file
          </Badge>
        )}
        {autoSpinnerBadge}
        {autoStatus === "autofixed" && (
          <>
            <Badge bg="success" className="mt-1">
              Auto-fixed with AI
            </Badge>
            {hasAnalysis && onReviewAnalysis && (
              <div className="mt-1">
                <Button
                  size="sm"
                  variant="outline-success"
                  onClick={onReviewAnalysis}
                  className="py-0"
                  style={{ fontSize: "0.75em" }}
                >
                  Review file analysis
                </Button>
              </div>
            )}
          </>
        )}
        {autoStatus === "multi_curve" && (
          <>
            <Badge bg="warning" text="dark" className="mt-1">
              Multi-curve file - review split
            </Badge>
            {onReviewSplit && (
              <div className="mt-1">
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={onReviewSplit}
                  className="py-0"
                  style={{ fontSize: "0.75em" }}
                >
                  Review split
                </Button>
              </div>
            )}
          </>
        )}
        {autoStatus === "failed_no_analysis" && (
          <Badge bg="danger" className="mt-1">
            Auto-process failed - AI analysis pending
          </Badge>
        )}
        {(autoStatus === "failed_with_analysis" ||
          autoStatus === "failed_with_updated_fix") && (
          <>
            <Badge bg="danger" className="mt-1">
              {autoStatus === "failed_with_updated_fix"
                ? `Auto-process failed - AI fix attempted and still failed - updated proposed fix available${attemptSuffix}`
                : "Auto-process failed - AI analysis completed - issue detected with file"}
            </Badge>
            <div className="mt-1 d-flex gap-1 flex-wrap">
              {onReviewAnalysis && (
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={onReviewAnalysis}
                  className="py-0"
                  style={{ fontSize: "0.75em" }}
                >
                  Review file analysis
                </Button>
              )}
              {hasProposedFix && onReprocessWithProposedFix && (
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={onReprocessWithProposedFix}
                  className="py-0"
                  style={{ fontSize: "0.75em" }}
                >
                  {autoStatus === "failed_with_updated_fix"
                    ? "Reprocess with updated fix"
                    : "Reprocess with proposed fix"}
                </Button>
              )}
            </div>
          </>
        )}
      </td>
      <td>
        {formatDate(file.uploadedAt)}
        {file.uploadedBy ? (
          <>
            <br />
            {file.uploadedBy}
          </>
        ) : (
          ""
        )}
      </td>
      <td>
        {formatDateTime(file.processedTime)}
        {file.processedBy ? (
          <>
            <br />
            {file.processedBy}
          </>
        ) : null}
        {file.lastAutoOperationKey && (
          <>
            <br />
            <span
              className="badge bg-success"
              title={
                file.autoFixed
                  ? "Processed via Auto mode (healed by an AI-proposed fix)"
                  : "Processed via Auto mode"
              }
            >
              Auto
            </span>
          </>
        )}
      </td>
      <td>
        {file.outputMetaData
          ? `${formatDateString(
              file.outputMetaData.input_data_start
            )} → ${formatDateString(file.outputMetaData.input_data_end)}`
          : "—"}
      </td>
      <td>
        {file.outputMetaData
          ? `${formatDateString(
              file.outputMetaData.fullyear_data_start
            )} → ${formatDateString(file.outputMetaData.fullyear_data_end)}`
          : "—"}
      </td>
      <td>
        {file.outputMetaData
          ? formatNumber(file.outputMetaData.typical_year_data_sum)
          : "—"}
      </td>
      <td>{renderFillCoverageBadge(coverageFraction)}</td>
      {showActions && (
        <td>
          <div style={{ display: "flex", gap: "4px" }}>
            <Button
              variant="primary"
              size="sm"
              onClick={() => onProcessClick?.(file)}
            >
              Process
            </Button>
            {isProcessed && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => onCommitClick?.(file)}
              >
                Commit
              </Button>
            )}
            <Button
              variant="danger"
              size="sm"
              onClick={() => onDeleteClick?.(file)}
            >
              Delete
            </Button>
          </div>
        </td>
      )}
    </tr>
  );
};

export default DemandFileSummaryRow;
